import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  TablePagination,
  Skeleton,
} from "@mui/material";

// =============================================================================
// DataTable — one reusable, server-paginated table for every list page.
//
// props:
//   columns: [{ field, headerName, align?, width?, render?(row, index) }]
//   rows:    array of row objects
//   loading: bool
//   getRowId?: (row) => key            (defaults to row._id / row.id / index)
//   onRowClick?: (row) => void
//   emptyText?: string
//   Pagination (all optional — omit to hide the footer):
//     page (0-based), rowsPerPage, total, onPageChange, onRowsPerPageChange
// =============================================================================
const DataTable = ({
  columns = [],
  rows = [],
  loading = false,
  getRowId,
  onRowClick,
  emptyText = "No records found",
  page,
  rowsPerPage = 20,
  total,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const showPagination =
    typeof page === "number" && typeof onPageChange === "function";

  const rowKey = (row, i) =>
    getRowId ? getRowId(row) : row?._id ?? row?.id ?? i;

  return (
    <Paper
      variant="outlined"
      sx={{ width: "100%", overflow: "hidden", borderRadius: "12px" }}
    >
      <TableContainer sx={{ maxHeight: "68vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  align={col.align || "left"}
                  sx={{ width: col.width, whiteSpace: "nowrap" }}
                >
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, r) => (
                <TableRow key={`sk-${r}`}>
                  {columns.map((col) => (
                    <TableCell key={col.field} align={col.align || "left"}>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ border: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 8,
                      color: "text.secondary",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2">{emptyText}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, i) => (
                <TableRow
                  hover
                  key={rowKey(row, i)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{ cursor: onRowClick ? "pointer" : "default" }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.field} align={col.align || "left"}>
                      {col.render
                        ? col.render(row, i)
                        : getNested(row, col.field) ?? "—"}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <TablePagination
          component="div"
          count={typeof total === "number" ? total : -1}
          page={page}
          onPageChange={(_, p) => onPageChange(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={
            onRowsPerPageChange
              ? (e) => onRowsPerPageChange(parseInt(e.target.value, 10))
              : undefined
          }
          rowsPerPageOptions={onRowsPerPageChange ? [10, 20, 50, 100] : []}
        />
      )}

      {loading && rows.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={18} />
        </Box>
      )}
    </Paper>
  );
};

// Supports dotted field paths like "customer.name".
function getNested(obj, path) {
  if (!path) return undefined;
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

export default DataTable;
